import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import Layout from './components/Layout';
import NotificationToast from './components/NotificationToast';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Traders from './pages/Traders';
import CopyConfigs from './pages/CopyConfigs';
import Positions from './pages/Positions';
import PaperTrading from './pages/PaperTrading';
import Markets from './pages/Markets';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import Tutorial from './pages/Tutorial';
import Blog from './pages/Blog';
import About from './pages/About';
import BlogPolymarketCopyTrading from './pages/BlogPolymarketCopyTrading';
import BlogPolymarketRiskManagement from './pages/BlogPolymarketRiskManagement';
import BlogPolymarketStrategies from './pages/BlogPolymarketStrategies';
import BlogBetMimicVsManual from './pages/BlogBetMimicVsManual';
import BlogPolymarketPlaybook from './pages/BlogPolymarketPlaybook';
import BlogPolymarketDegenCopyTrading from './pages/BlogPolymarketDegenCopyTrading';
import BlogPolymarketSportsDegens from './pages/BlogPolymarketSportsDegens';
import BlogPolymarketTradingTool from './pages/BlogPolymarketTradingTool';
import BlogPolymarketTools from './pages/BlogPolymarketTools';
import BlogPolymarketPaperTrading from './pages/BlogPolymarketPaperTrading';
import BlogPolymarketExitDiscipline from './pages/BlogPolymarketExitDiscipline';
import BlogPolymarketChoosingTraders from './pages/BlogPolymarketChoosingTraders';
import BlogPolymarketResolutionRedeem from './pages/BlogPolymarketResolutionRedeem';
import BlogPolymarketUltimateGuide from './pages/BlogPolymarketUltimateGuide';
import BlogPolymarketTradeAlerts from './pages/BlogPolymarketTradeAlerts';
import BlogUnlockingCopyTrading from './pages/BlogUnlockingCopyTrading';
import Legal from './pages/Legal';
import LegalAccept from './pages/LegalAccept';
import WhaleVault from './pages/WhaleVault';
import Arbitrage from './pages/Arbitrage';
import DegenMode from './pages/DegenMode';
import Help from './pages/Help';
import Referrals from './pages/Referrals';
import Admin from './pages/Admin';
import { CURRENT_TERMS_VERSION } from './config/legal';
import { useParams } from 'react-router-dom';

// Referral redirect component
function ReferralRedirect() {
  const { code } = useParams();
  return <Navigate to={`/register?ref=${code}`} replace />;
}

function ProtectedRoute({ children, requireVerified = false, requireLegal = true }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect unverified users to verification page (except for verify-email page itself)
  if (requireVerified && !user?.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  if (requireLegal) {
    const acceptedAt = user?.legal?.termsAcceptedAt;
    const acceptedVersion = user?.legal?.termsVersion;
    const accepted = !!acceptedAt && acceptedVersion === CURRENT_TERMS_VERSION;
    if (!accepted) {
      return <Navigate to="/legal/accept" replace />;
    }
  }
  
  return children;
}

function App() {
  return (
    <>
      {/* Global toast notifications */}
      <NotificationToast />
      
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Public blog routes for SEO */}
      <Route path="/about" element={<About />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/blog/polymarket-copy-trading" element={<BlogPolymarketCopyTrading />} />
      <Route path="/blog/polymarket-risk-management" element={<BlogPolymarketRiskManagement />} />
      <Route path="/blog/polymarket-copy-trading-strategies" element={<BlogPolymarketStrategies />} />
      <Route path="/blog/betmimic-vs-manual-polymarket" element={<BlogBetMimicVsManual />} />
      <Route path="/blog/polymarket-copy-trading-playbook" element={<BlogPolymarketPlaybook />} />
      <Route path="/blog/polymarket-degen-copy-trading" element={<BlogPolymarketDegenCopyTrading />} />
      <Route path="/blog/polymarket-sports-betting-degens" element={<BlogPolymarketSportsDegens />} />
      <Route path="/blog/polymarket-trading-tool" element={<BlogPolymarketTradingTool />} />
      <Route path="/blog/polymarket-tools" element={<BlogPolymarketTools />} />
      <Route path="/blog/unlocking-copy-trading-polymarket" element={<BlogUnlockingCopyTrading />} />
      <Route path="/blog/polymarket-paper-trading" element={<BlogPolymarketPaperTrading />} />
      <Route path="/blog/polymarket-exit-discipline" element={<BlogPolymarketExitDiscipline />} />
      <Route path="/blog/polymarket-choosing-traders" element={<BlogPolymarketChoosingTraders />} />
      <Route path="/blog/polymarket-resolution-redeem" element={<BlogPolymarketResolutionRedeem />} />
      <Route path="/blog/polymarket-trade-alerts" element={<BlogPolymarketTradeAlerts />} />
      <Route path="/blog/polymarket-ultimate-guide" element={<BlogPolymarketUltimateGuide />} />

      {/* Referral redirect - /r/:code redirects to /register?ref=code */}
      <Route path="/r/:code" element={<ReferralRedirect />} />

      {/* Admin panel - separate auth, not part of main app */}
      <Route path="/admin" element={<Admin />} />

      <Route
        path="/legal/accept"
        element={
          <ProtectedRoute requireLegal={false}>
            <LegalAccept />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verify-email"
        element={
          <ProtectedRoute>
            <VerifyEmail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute requireVerified>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/tutorial" element={<Tutorial />} />
                <Route path="/traders" element={<Traders />} />
                <Route path="/copy-configs" element={<CopyConfigs />} />
                <Route path="/positions" element={<Positions />} />
                <Route path="/paper" element={<PaperTrading />} />
                <Route path="/markets" element={<Markets />} />
                <Route path="/whale-vault" element={<WhaleVault />} />
                <Route path="/arbitrage" element={<Arbitrage />} />
                <Route path="/degen" element={<DegenMode />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/help" element={<Help />} />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
}

export default App;
